// Copied from platform-app components/pos/customerFacing/SeasonalEffectOverlay.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React from 'react';

// ── Effect registry ──────────────────────────────────────────────────────────
// Keys MUST match AGENTIC_POS device side exactly.
const REGISTRY: Record<string, { emojis: string[]; motion: 'fall' | 'rise' | 'twinkle' | 'drift' }> = {
  snow:      { emojis: ['❄️', '❄', '🌨️'],              motion: 'fall'    },
  confetti:  { emojis: ['🎉', '🎊', '✨', '🎈'],         motion: 'fall'    },
  hearts:    { emojis: ['💕', '💗', '❤️', '💖'],         motion: 'rise'    },
  fireworks: { emojis: ['🎆', '🎇', '✨', '💥'],          motion: 'twinkle' },
  leaves:    { emojis: ['🍂', '🍁', '🍃'],               motion: 'fall'    },
  halloween: { emojis: ['🦇', '🕷️', '🎃', '👻'],         motion: 'drift'   },
  shamrocks: { emojis: ['☘️', '🍀'],                     motion: 'fall'    },
};

// ── Keyframe definitions injected once ──────────────────────────────────────
const KEYFRAMES = `
@keyframes cfx-fall {
  0%   { transform: translateY(-10%) rotate(0deg);   opacity: 1; }
  100% { transform: translateY(110%) rotate(360deg); opacity: 0.6; }
}
@keyframes cfx-rise {
  0%   { transform: translateY(110%) rotate(0deg);   opacity: 0.8; }
  100% { transform: translateY(-10%) rotate(-360deg); opacity: 0; }
}
@keyframes cfx-twinkle {
  0%,100% { transform: scale(1);   opacity: 1; }
  50%     { transform: scale(1.4); opacity: 0.3; }
}
@keyframes cfx-drift {
  0%   { transform: translate(0,    0)    rotate(0deg); }
  25%  { transform: translate(12px, 18px) rotate(15deg); }
  50%  { transform: translate(-8px, 36px) rotate(-10deg); }
  75%  { transform: translate(10px, 54px) rotate(20deg); }
  100% { transform: translate(0,    72px) rotate(0deg); opacity: 0; }
}
`;

let styleInjected = false;
function injectKeyframes() {
  if (styleInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.setAttribute('data-cfx', '1');
  el.textContent = KEYFRAMES;
  document.head.appendChild(el);
  styleInjected = true;
}

// ── Component ────────────────────────────────────────────────────────────────
const PARTICLE_COUNT = 16;

interface Props {
  effect?: string;
}

export default function SeasonalEffectOverlay({ effect }: Props) {
  if (!effect || effect === 'none') return null;

  const entry = REGISTRY[effect];
  if (!entry) return null;

  injectKeyframes();

  const { emojis, motion } = entry;
  const animName = `cfx-${motion}`;

  return (
    <div
      data-testid="cfx-overlay"
      data-effect={effect}
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 10,
      }}
    >
      {Array.from({ length: PARTICLE_COUNT }, (_, i) => {
        const left = (i * 61) % 100;
        const top = motion === 'twinkle' ? ((i * 47) % 90) : undefined;
        const delay = ((i * 0.37) % 3).toFixed(2);
        const duration = (2.5 + ((i * 0.53) % 3)).toFixed(2);
        const fontSize = 14 + ((i * 7) % 16);
        const emoji = emojis[i % emojis.length];

        return (
          <span
            key={i}
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: `${left}%`,
              top: motion === 'twinkle' ? `${top}%` : '-5%',
              fontSize,
              lineHeight: 1,
              animation: `${animName} ${duration}s ${delay}s ease-in-out infinite`,
              willChange: 'transform, opacity',
              userSelect: 'none',
            }}
          >
            {emoji}
          </span>
        );
      })}
    </div>
  );
}
